import {find, map, replace} from 'lodash';
import moment from 'moment';
import {useCallback, useContext, useMemo} from 'react';
import {useTranslation} from 'react-i18next';

import {ACTIVITY_NAME_TRANSLATE} from 'constants/session';
import {UserContext} from 'context';
import {TimeFormatSpecificity} from 'enums';
import {AnyParticipant, SessionEvent, SessionActivityName, AccountAssignmentInfo, Group} from 'interfaces';
import {TRANSLATION_KEYS} from 'locale';
import {getAssignmentName} from 'services';
import {PlatformInfo, SessionAudit, ParticipantRole} from 'types';
import {dateUtils, sessionUtils} from 'utils';
import {buildTranslationKey, getParticipantName} from 'utils/transform';

const SESSION_T_KEYS = TRANSLATION_KEYS.COMMON.SESSION;
const T_KEYS = TRANSLATION_KEYS.USER.ACTIVITY.SESSION_HISTORY.AUDIT;

enum SessionEventName {
    AddScreenshot = 'add_screenshot',
    DeleteScreenshot = 'delete_screenshot',
    AddParticipant = 'add_participant',
    AddParticipantError = 'add_participant_error',
    RemoveParticipant = 'remove_participant',
    ReassignToAll = 'reassign_to_all',
    SetStatus = 'set_status',
    /**
     * @deprecated. remove after 30+ days after release
     */
    SetParticipantVerificationStatus = 'set_participant_verification_status',
    SetAsyncRequestStatus = 'set_async_request_status',
    StartActivity = 'start_activity',
    StartActivityError = 'start_activity_error',
    AddActivityData = 'add_activity_data',
    StopActivityError = 'stop_activity_error',
    FinishActivity = 'finish_activity',
    ParticipantNotification = 'participant_notification',
    ChangeAssignment = 'change_assignment',
    ChangeAssignmentError = 'change_assignment_error',
    SetSocketId = 'set_socket_id',
    RefreshSocketDate = 'refresh_socket_date',
}

enum ParticipantNotification {
    ClientEvent = 'client_event'
}

const HIDE_EVENTS: string[] = [SessionEventName.RefreshSocketDate];

type Props = {
    audit: SessionAudit;
    groups: Group[];
};

export const SessionAuditInfo: React.FC<Props> = ({audit, groups}: Props) => {
    const {t} = useTranslation();
    const {settings: {common: {timeZone, dateFormat, timeFormat}}} = useContext(UserContext);

    const dateTimeFormat = useMemo(() =>
        dateUtils.getDateTimeFormat(dateFormat, timeFormat, TimeFormatSpecificity.Milliseconds),
    [dateFormat, timeFormat]);

    const formatDate = useCallback((dateToFormat: moment.MomentInput) =>
        moment(dateToFormat).tz(timeZone).format(dateTimeFormat), [dateTimeFormat, timeZone]);

    const getParticipantNameByRole = useCallback((participant) =>
        sessionUtils.isInviteeParticipant(participant) ?
            getParticipantName(participant.data.userInfo, participant.data.userInfo.id) :
            getParticipantName(participant.data, participant.data?.id), []);

    const getSessionParticipantName = useCallback((participantId: string) => {
        const id = participantId;
        const participant = find(audit.participants, {id: participantId});
        return participant ? getParticipantNameByRole(participant) : id;
    }, [audit, getParticipantNameByRole]);

    const getParticipantLastActivity = useCallback((participantId: string) => {
        const participantDynamicData = audit.dynamicData?.participants?.[participantId];
        return participantDynamicData?.lastActivity ? formatDate(participantDynamicData.lastActivity) : null;
    }, [audit, formatDate]);

    const translateUpper = useCallback((key: string, tKeys: {[key: string]: any}, defaultValue?: string) => {
        const tKey = buildTranslationKey(key);
        return tKeys[tKey] ? t(tKeys[tKey]) : (defaultValue || key);
    }, [t]);

    const renderParticipantNotification = useCallback((event: SessionEvent) => {
        const type = event.data.name;
        const name = translateUpper(type, T_KEYS.PARTICIPANT_NOTIFICATION);
        const rows = [
            <div key='type'>{`${t(T_KEYS.DETAIL.TYPE)}: ${name}`}</div>
        ];
        if (type === ParticipantNotification.ClientEvent) {
            rows.push(
                <div key='method'>
                    {`${t(T_KEYS.DETAIL.NAME)}: ${event.data.data.name}`}
                    {event.data.data.description && ` (${event.data.data.description})`}
                </div>,
                <div key='notified'>
                    {`${t(T_KEYS.DETAIL.NOTIFIED)}: ${event.data.data.notified ? t(T_KEYS.DETAIL.YES) : t(T_KEYS.DETAIL.NO)}`}
                </div>,
            );
        }
        return rows;
    }, [t, translateUpper]);

    const renderAddActivityData = useCallback((event: SessionEvent) => {
        const activityName = event.data.name;
        const rows = [
            <div key='name'>{`${t(T_KEYS.DETAIL.NAME)}: ${t(ACTIVITY_NAME_TRANSLATE[activityName])}`}</div>
        ];

        if ([SessionActivityName.Audio, SessionActivityName.AudioVideo, SessionActivityName.ScreenSharing].includes(activityName)) {
            const connection = event.data.data?.connection;
            if (connection) {
                rows.push(
                    <div key='status'>
                        {`${t(T_KEYS.DETAIL.CONNECTION)}: ${translateUpper(connection.status, T_KEYS.CONNECTION_STATUS)}`}
                    </div>,
                    connection.attempt && <div key='attempt'>{`${t(T_KEYS.DETAIL.ATTEMPT)}: ${connection.attempt}`}</div>,
                );
                if (connection.code) {
                    rows.push(
                        <div key='details'>{`${t(T_KEYS.DETAIL.DETAILS)}: ${connection.code} (${connection.message})`}</div>,
                    );
                }
            }
        }

        return rows;
    }, [t, translateUpper]);

    const renderRequestStatus = useCallback((status: string) =>
        translateUpper(status, SESSION_T_KEYS.REQUEST_STATUS, t(SESSION_T_KEYS.REQUEST_STATUS.INITIAL)), [t, translateUpper]);

    const renderAssignment = useCallback((event: SessionEvent<{assignment: AccountAssignmentInfo}>) =>
        getAssignmentName(groups, event.data?.assignment?.groupId, event.data?.assignment?.subgroupId), [groups]);

    const renderSocketConnection = useCallback((event: SessionEvent<{socketId: string}>) =>
        `${t(T_KEYS.DETAIL.CONNECTED)}: ${event.data.socketId ? t(T_KEYS.DETAIL.YES) : t(T_KEYS.DETAIL.NO)}`, [t]);

    const renderEventDetails = useCallback((event: SessionEvent) => {
        switch (event.name) {
            case SessionEventName.AddScreenshot:
                return <div>{`${t(T_KEYS.DETAIL.NAME)}: ${event.data.screenshotName}`}</div>;
            case SessionEventName.DeleteScreenshot:
                return <div>{`${t(T_KEYS.DETAIL.NAME)}: ${event.data.screenshotName}`}</div>;
            case SessionEventName.AddParticipant:
                return <div>{`${t(SESSION_T_KEYS.LABELS.PARTICIPANT)}: ${getParticipantNameByRole(event.data)}`}</div>;
            case SessionEventName.AddParticipantError:
                return <div>{`${t(SESSION_T_KEYS.LABELS.PARTICIPANT)}: ${event.data.email}`}</div>;
            case SessionEventName.RemoveParticipant:
                return <div>{`${t(SESSION_T_KEYS.LABELS.PARTICIPANT)}: ${getParticipantNameByRole(event.data)}`}</div>;
            case SessionEventName.ReassignToAll:
                return <div>{`${t(SESSION_T_KEYS.LABELS.PARTICIPANT)}: ${getParticipantNameByRole(event.data)}`}</div>;
            case SessionEventName.SetStatus:
                return <div>{`${t(SESSION_T_KEYS.LABELS.STATUS)}: ${t(SESSION_T_KEYS.STATUS[event.data])}`}</div>;
            case SessionEventName.SetParticipantVerificationStatus:
                return <div>{`${t(SESSION_T_KEYS.LABELS.STATUS)}: ${renderRequestStatus(event.data.status)}`}</div>;
            case SessionEventName.SetAsyncRequestStatus:
                return [
                    <div key='type'>
                        {`${t(T_KEYS.DETAIL.TYPE)}: ${t(SESSION_T_KEYS.ASYNC_REQUEST.TYPE[buildTranslationKey(event.data.type)])}`}
                    </div>,
                    <div key='status'>{`${t(SESSION_T_KEYS.LABELS.STATUS)}: ${renderRequestStatus(event.data.status)}`}</div>,
                ];
            case SessionEventName.StartActivity:
                return <div>{`${t(T_KEYS.DETAIL.NAME)}: ${t(ACTIVITY_NAME_TRANSLATE[event.data.name])}`}</div>;
            case SessionEventName.StartActivityError:
                return <div>{`${t(T_KEYS.DETAIL.ERROR)}: ${t(ACTIVITY_NAME_TRANSLATE[event.data.name])}`}</div>;
            case SessionEventName.StopActivityError:
                return <div>{`${t(T_KEYS.DETAIL.ERROR)}: ${t(ACTIVITY_NAME_TRANSLATE[event.data.name])}`}</div>;
            case SessionEventName.FinishActivity:
                return <div>{`${t(T_KEYS.DETAIL.NAME)}: ${t(ACTIVITY_NAME_TRANSLATE[event.data.name])}`}</div>;
            case SessionEventName.ParticipantNotification:
                return renderParticipantNotification(event);
            case SessionEventName.AddActivityData:
                return renderAddActivityData(event);
            case SessionEventName.ChangeAssignment:
                return renderAssignment(event);
            case SessionEventName.ChangeAssignmentError:
                return renderAssignment(event);
            case SessionEventName.SetSocketId:
                return renderSocketConnection(event);
            default: return null;
        }
    }, [t, renderParticipantNotification, renderRequestStatus, renderAddActivityData, getParticipantNameByRole, renderAssignment,
        renderSocketConnection]);

    const renderEvent = useCallback((event: SessionEvent) => {
        const participantName = event.participantId ? getSessionParticipantName(event.participantId) : null;
        if (event.name === SessionEventName.SetStatus && !SESSION_T_KEYS.STATUS[event.data]) {
            return;
        }
        if (HIDE_EVENTS.includes(event.name)) {
            return;
        }
        return (
            <div>
                <div className='tw-font-bold'>{translateUpper(event.name, T_KEYS.EVENT)}</div>
                {participantName && <div>{`${t(SESSION_T_KEYS.LABELS.PARTICIPANT)}: ${participantName}`}</div>}
                {renderEventDetails(event)}
                <div>{formatDate(event.date)}</div>
            </div>
        );
    }, [t, formatDate, translateUpper, renderEventDetails, getSessionParticipantName]);

    const renderParticipantPlatform = useCallback((platformInfo: PlatformInfo) => [
        platformInfo?.os &&
            <div key='os'>
                {`${t(SESSION_T_KEYS.LABELS.OS)}: ${platformInfo.os} ${platformInfo.osVersion || ''}`}
            </div>,
        platformInfo?.browser &&
            <div key='browser'>
                {`${t(SESSION_T_KEYS.LABELS.BROWSER)}: ${platformInfo.browser} ${platformInfo.browserVersion || ''}`}
            </div>
    ], [t]);

    const renderParticipant = useCallback((participant: AnyParticipant) => {
        const lastActivity = getParticipantLastActivity(participant.id);
        // TODO: fix guest (refactor and unify)
        if (participant.role === ParticipantRole.Client) {
            return [
                <div key='role' className='tw-font-bold'>
                    {`${t(SESSION_T_KEYS.LABELS.ROLE)}: ${t(SESSION_T_KEYS.LABELS.CLIENT)}`}
                </div>,
                <div key='id'>{`${t(SESSION_T_KEYS.LABELS.ID)}: ${participant.data.userInfo.id}`}</div>,
                <div key='name'>{`${t(SESSION_T_KEYS.LABELS.NAME)}: ${getSessionParticipantName(participant.id)}`}</div>,
                <div key='location'>{`${t(SESSION_T_KEYS.LABELS.LOCATION)}: ${participant.data.location || ''}`}</div>,
                <div key='device'>
                    {`${t(SESSION_T_KEYS.LABELS.DEVICE)}: ` +
                    `${translateUpper(replace(participant.data.device, '-', '_'), SESSION_T_KEYS.CLIENT_DEVICE)}`}
                </div>,
                ...renderParticipantPlatform(participant.data.clientInfo),
                lastActivity &&
                    <div key='last-activity'>
                        {`${t(SESSION_T_KEYS.LABELS.LAST_ACTIVITY)}: ${lastActivity}`}
                    </div>,
            ];
        } else if (participant.role === ParticipantRole.Guest) {
            return [
                <div key='role' className='tw-font-bold'>
                    {`${t(SESSION_T_KEYS.LABELS.ROLE)}: ${t(SESSION_T_KEYS.LABELS.GUEST)}`}
                </div>,
                <div key='id'>{`${t(SESSION_T_KEYS.LABELS.ID)}: ${participant.data.userInfo.id}`}</div>,
                <div key='name'>{`${t(SESSION_T_KEYS.LABELS.NAME)}: ${getSessionParticipantName(participant.id)}`}</div>,
                <div key='location'>{`${t(SESSION_T_KEYS.LABELS.LOCATION)}: ${participant.data.location || ''}`}</div>,
                <div key='device'>
                    {`${t(SESSION_T_KEYS.LABELS.DEVICE)}: ` +
                    `${translateUpper(replace(participant.data.device, '-', '_'), SESSION_T_KEYS.CLIENT_DEVICE)}`}
                </div>,
                ...renderParticipantPlatform(participant.data.clientInfo),
                lastActivity &&
                    <div key='last-activity'>
                        {`${t(SESSION_T_KEYS.LABELS.LAST_ACTIVITY)}: ${lastActivity}`}
                    </div>,
            ];
        } else if (participant.role === ParticipantRole.Agent) {
            return [
                <div key='role' className='tw-font-bold'>
                    {`${t(SESSION_T_KEYS.LABELS.ROLE)}: ${t(SESSION_T_KEYS.LABELS.AGENT)}`}
                </div>,
                <div key='name'>{`${t(T_KEYS.DETAIL.NAME)}: ${getSessionParticipantName(participant.id)}`}</div>,
                ...renderParticipantPlatform(participant.data.platformInfo),
                lastActivity &&
                    <div key='last-activity'>
                        {`${t(SESSION_T_KEYS.LABELS.LAST_ACTIVITY)}: ${lastActivity}`}
                    </div>,
            ];
        } else if (participant.role === ParticipantRole.Assistant) {
            return [
                <div key='role' className='tw-font-bold'>
                    {`${t(SESSION_T_KEYS.LABELS.ROLE)}: ${t(SESSION_T_KEYS.LABELS.ASSISTANT)}`}
                </div>,
                <div key='name'>{`${t(T_KEYS.DETAIL.NAME)}: ${getSessionParticipantName(participant.id)}`}</div>,
                ...renderParticipantPlatform(participant.data.platformInfo),
                lastActivity &&
                    <div key='last-activity'>
                        {`${t(SESSION_T_KEYS.LABELS.LAST_ACTIVITY)}: ${lastActivity}`}
                    </div>,
            ];
        }
    }, [t, translateUpper, getSessionParticipantName, getParticipantLastActivity, renderParticipantPlatform]);

    return (
        <div>
            <div className='tw-mb-6'>
                <div className='tw-text-default tw-font-bold tw-mb-1'>{t(SESSION_T_KEYS.LABELS.PARTICIPANTS)}</div>
                <div>{map(audit.participants, (p) => <div key={p.id} className='tw-mb-2'>{renderParticipant(p)}</div>)}</div>
            </div>
            <div>
                <div className='tw-text-default tw-font-bold tw-mb-1'>{t(SESSION_T_KEYS.LABELS.EVENTS)}</div>
                {map(audit.events, (event) => (
                    <div key={event.id} className='tw-mb-2'>
                        <div>{renderEvent(event)}</div>
                    </div>
                ))}
            </div>
        </div>
    );
};
